import axios, { ResponseType, AxiosRequestHeaders } from 'axios';
import { setInterceptors } from './interceptors';

export const {
  REACT_APP_BASE_URL: BASE_URL,
  REACT_APP_DEV_FRONTEND_URL: DEV_FRONTEND_URL,
  REACT_APP_IS_LOCALHOST,
} = process.env;

type RequestParamsType = {
  url: string;
  responseType?: ResponseType;
  params?: URLSearchParams;
  headers?: AxiosRequestHeaders;
};

type RequestWithFormData = RequestParamsType & {
  body: FormData;
};

type RequestWithBodyType = RequestParamsType & {
  body?: unknown;
  headers?: AxiosRequestHeaders;
};

type IStandardResponseFromBE<T> = {
  code: number;
  content: T;
};

export type IStandardErrorResponseFromBE = {
  code: number;
  errorMessage: string;
};

export type IResponse<T> = {
  data: IStandardResponseFromBE<T>;
};

axios.defaults.withCredentials = true;

export const HTTP = axios.create({
  baseURL: REACT_APP_IS_LOCALHOST ? '/benefits' : BASE_URL,
});

setInterceptors(HTTP);

export const post = <T>({
  url,
  body,
  params,
}: RequestWithBodyType): Promise<T> =>
  HTTP.post(url, body, { params }).then(
    ({ data }: IResponse<T>) => data.content,
  );

export const postWithFormData = <T>({
  url,
  body,
}: RequestWithFormData): Promise<T> =>
  HTTP.post(url, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(({ data }: IResponse<T>) => data.content);

export const patch = <T>({
  url,
  body,
  params,
}: RequestWithBodyType): Promise<T> =>
  HTTP.patch(url, body, { params }).then(
    ({ data }: IResponse<T>) => data.content,
  );

export const put = <T>({
  url,
  body,
  params,
}: RequestWithBodyType): Promise<T> =>
  HTTP.put(url, body, { params }).then(
    ({ data }: IResponse<T>) => data.content,
  );

export const get = <T>({ url, params }: RequestParamsType): Promise<T> =>
  HTTP.get(url, { params }).then(({ data }: IResponse<T>) => data.content);

export const deleteEntity = <T>(url: string): Promise<T> =>
  HTTP.delete(url).then(({ data }: IResponse<T>) => data.content);

export const downloadFile = ({
  url,
  params,
  filename,
}: RequestParamsType & { filename?: string }): Promise<void> =>
  HTTP.get(url, { responseType: 'blob', params }).then(({ headers, data }) => {
    const href = URL.createObjectURL(data);

    const ensuredFilename =
      filename ??
      headers['content-disposition']
        ?.split('=')
        .at(-1)
        ?.replace(/(^"|"$)/g, '') ??
      '';

    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', ensuredFilename);
    link.click();

    URL.revokeObjectURL(href);
  });
